/**
 * Is Cora healthy? Her configuration, every hourly run, the positions she could not screen and
 * why, the screenings that failed — and what she costs. The silent failures of the Make/Relevance
 * setup (a malformed posting ID stopping a position for weeks) show up here instead.
 */
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { fetchCoraTicks, type CoraRunSummary, type CoraStats, type CoraStatus } from '../../services/coraApi';
import { ISSUE_LABEL, fmtSeconds, fmtUsd, fmtWhen } from './coraFormat';
import DocsConnection from './DocsConnection';

function Check({ ok, label, hint }: { ok: boolean; label: string; hint: string }) {
  return (
    <li className="flex gap-2 text-sm">
      <span aria-hidden>{ok ? '✅' : '⚠️'}</span>
      <span className="text-zinc-900">{label}</span>
      {!ok && <span className="text-zinc-600">— {hint}</span>}
    </li>
  );
}

interface Props {
  status: CoraStatus | null;
  stats: CoraStats | null;
  runs: CoraRunSummary[];
  onOpen: (id: string) => void;
}

export default function OperationsTab({ status, stats, runs, onOpen }: Props) {
  const ticks = useQuery({ queryKey: ['cora', 'ticks'], queryFn: fetchCoraTicks, staleTime: 60_000 });
  const latest = ticks.data?.ticks[0];
  const failing = runs.filter((r) => r.status === 'failed' || r.status === 'write_failed');

  return (
    <div className="flex flex-col gap-6">
      <DocsConnection />
      {status && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-zinc-900">Configuration</h2>
          <ul className="flex flex-col gap-1">
            <Check ok={status.mode !== 'off'} label={`Mode: ${status.mode}`} hint="Cora is not screening (CORA_MODE)." />
            <Check ok={status.docsConfigured} label="Scoring documents readable" hint="CORA_DOCS_IMPERSONATE is not set." />
            <Check ok={status.performAsConfigured} label="Lever writes attributed to a Lever user" hint="Notes are posted as the API key (CORA_LEVER_PERFORM_AS)." />
          </ul>
          <p className="mt-2 text-xs text-zinc-500">Model {status.model} · prompt {status.promptVersion}</p>
        </section>
      )}

      {stats && (
        <section className="grid gap-3 text-sm md:grid-cols-4">
          <div className="rounded-sm border border-solid border-pearl p-3">Screenings: <strong>{stats.total}</strong></div>
          <div className="rounded-sm border border-solid border-pearl p-3">Failed: <strong>{(stats.statuses.failed ?? 0) + (stats.statuses.write_failed ?? 0)}</strong></div>
          <div className="rounded-sm border border-solid border-pearl p-3">Model cost: <strong>{fmtUsd(stats.costUsd)}</strong></div>
          <div className="rounded-sm border border-solid border-pearl p-3">Avg. time: <strong>{fmtSeconds(stats.avgDurationMs)}</strong></div>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-zinc-900">Positions Cora could not screen{latest ? ` (run of ${fmtWhen(latest.at)})` : ''}</h2>
        {!latest ? (
          <p className="text-sm text-zinc-600">{ticks.isLoading ? 'Loading…' : 'No run recorded yet.'}</p>
        ) : latest.issues.length === 0 ? (
          <p className="text-sm text-zinc-600">Every position ticked for Cora was screened.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {latest.issues.map((i) => (
              <li key={`${i.positionId}-${i.issue}`} className="text-zinc-700">
                <strong className="text-zinc-900">{i.title || 'Untitled'} (#{i.positionId})</strong>: {ISSUE_LABEL[i.issue] ?? i.issue}
                {i.detail ? ` — ${i.detail}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>

      {failing.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-red">Screenings that need attention ({failing.length})</h2>
          <ul className="flex flex-col gap-1 text-sm">
            {failing.map((r) => (
              <li key={r.id}>
                <Button variant="ghost" className="h-auto p-1 text-left text-sm font-normal underline" onClick={() => onOpen(r.id)}>
                  {r.candidateName} — {r.positionTitle}: {r.error || r.status}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-zinc-900">Hourly runs</h2>
        {ticks.isError && <p role="alert" className="text-sm text-red">Runs could not be loaded.</p>}
        <div className="overflow-x-auto rounded-sm border border-solid border-pearl">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-solid border-pearl text-xs uppercase text-zinc-500">
              <tr>
                {['When', 'Mode', 'Positions', 'In "New applicant"', 'Screened before', 'Screened', 'Failed', 'Deferred', 'Issues', 'Took'].map((h) => (
                  <th key={h} className="px-3 py-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(ticks.data?.ticks ?? []).map((t) => (
                <tr key={t.id} className="border-b border-solid border-pearl last:border-0 text-zinc-700">
                  <td className="whitespace-nowrap px-3 py-2">{fmtWhen(t.at)}</td>
                  <td className="px-3 py-2">{t.mode}</td>
                  <td className="px-3 py-2">{t.positions}</td>
                  <td className="px-3 py-2">{t.candidatesFound}</td>
                  {/* Cora never moves anyone, so the stage keeps everyone she already noted. */}
                  <td className="px-3 py-2">{t.skippedByTag + t.alreadyHandled}</td>
                  <td className="px-3 py-2">{(t.byStatus.screened ?? 0) + (t.byStatus.no_resume ?? 0)}</td>
                  <td className="px-3 py-2">{(t.byStatus.failed ?? 0) + (t.byStatus.write_failed ?? 0)}</td>
                  <td className="px-3 py-2">{t.deferred}</td>
                  <td className="px-3 py-2">{t.issues.length}</td>
                  <td className="px-3 py-2">{fmtSeconds(t.durationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
